/**
 * Cloudflare Turnstile, for sites that ask anonymous visitors to prove they
 * are a browser (backend net/turnstile.ts, migration 0055).
 *
 * 🔴 NOTHING HAPPENS UNTIL /v1/config SENDS A SITE KEY, and it only does that
 *    for a site somebody opted in AND a server that can verify the answer. On
 *    every other site this module loads no script, adds no element and makes
 *    no request: a third-party script on somebody else's page is not something
 *    to ship by default.
 *
 * 🔴 RENDERED IN THE LIGHT DOM, NOT OUR SHADOW ROOT. The panel is a shadow
 *    root (panel.ts), and Turnstile renders an iframe and talks to it across
 *    the document; inside a shadow root that is unsupported and fails in ways
 *    that differ by browser. So the container goes on document.body, off
 *    screen, and Turnstile puts its own overlay in front when it needs the
 *    person to do something.
 *
 * 🔴 A TOKEN IS GOOD ONCE. Cloudflare rejects a replayed token, which is the
 *    point of it, so one is fetched per turn rather than kept.
 */

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let loading: Promise<TurnstileApi | null> | null = null;

/** Loads Cloudflare's script once, resolving null if it never arrives. */
function load(): Promise<TurnstileApi | null> {
  if (loading) return loading;
  loading = new Promise<TurnstileApi | null>((resolve) => {
    if (window.turnstile) return resolve(window.turnstile);
    const el = document.createElement("script");
    el.src = SCRIPT;
    el.async = true;
    el.defer = true;
    /**
     * 🔴 A BLOCKED SCRIPT RESOLVES null RATHER THAN HANGING. A customer's
     *    content-security policy can refuse this, and a promise that never
     *    settles would leave somebody watching a cursor blink forever. The
     *    server decides what a missing token means; this only reports one.
     */
    el.onerror = () => resolve(null);
    el.onload = () => resolve(window.turnstile ?? null);
    document.head.appendChild(el);
  });
  return loading;
}

/**
 * One token for one turn, or null if Turnstile could not produce one.
 *
 * Resolves null rather than throwing: a caller that cannot get a token still
 * sends the turn, and the server refuses it if the site requires one. That
 * keeps the decision in one place instead of two.
 */
export async function turnstileToken(siteKey: string, timeoutMs = 20_000): Promise<string | null> {
  const api = await load();
  if (!api) return null;

  const holder = document.createElement("div");
  // Off screen rather than display:none: Turnstile needs a real box to render
  // into, and a hidden one makes it refuse. Its own challenge overlay, when one
  // is needed, is fixed-position and unaffected by where this sits.
  holder.style.cssText = "position:absolute;left:-9999px;top:0;width:300px;height:65px;";
  document.body.appendChild(holder);

  return new Promise<string | null>((resolve) => {
    let done = false;
    let id: string | undefined;
    const finish = (token: string | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      try {
        if (id !== undefined) api.remove(id);
      } catch {
        // Already gone, or Turnstile tore it down itself. Nothing to do.
      }
      holder.remove();
      resolve(token);
    };
    // A person who never finishes an interactive challenge, or a challenge that
    // never appears, must not hold the turn open indefinitely.
    const timer = setTimeout(() => finish(null), timeoutMs);

    try {
      id = api.render(holder, {
        sitekey: siteKey,
        // The marker the Turnstile setup flow stamps on every widget it writes.
        action: "turnstile-spin-v2",
        callback: (token: string) => finish(token),
        "error-callback": () => finish(null),
        "timeout-callback": () => finish(null),
        "expired-callback": () => finish(null),
      });
    } catch {
      finish(null);
    }
  });
}
